import React, { useState, useEffect, useMemo } from "react";
import {
  faPlayCircle,
  faUpload,
  faUser,
  faStar,
  faList,
  faSearch,
  faTimes,
  faEdit,
  faChartBar,
  faDollarSign,
  faEnvelope,
  faCog,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Sub-component: Header
const Header = () => (
  <header className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-900">
    <h1 className="text-2xl font-bold text-blue-400">▶️ foodTube</h1>
    <span className="text-yellow-200 text-sm italic">
      Your world of wisdom in motion
    </span>
  </header>
);

// Sub-component: Hero Banner
// const Hero = () => (
//   <section className="p-4 text-center bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
//     <h2 className="text-xl sm:text-2xl text-white font-semibold mb-2">
//       Discover. Learn. Grow.
//     </h2>
//     <p className="text-gray-400 text-sm">
//       Explore wisdom through stories, mentors, and creators.
//     </p>
//   </section>
// );

// Sub-component: Search Bar
const SearchBar = ({ onSearch, placeholder = "Search wisdom..." }) => {
  const [query, setQuery] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(query);
  };

  return (
    <form onSubmit={handleSubmit} className="relative mx-4 mb-4">
      <FontAwesomeIcon
        icon={faSearch}
        className="text-yellow-200 mr-4"
      />
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white outline-none focus:border-blue-500 transition"
        aria-label="Search videos"
      />
      {query && (
        <button
          type="button"
          onClick={() => {
            setQuery("");
            onSearch("");
          }}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </form>
  );
};

// Sub-component: Video Card
const VideoCard = ({ video, onClick }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => e.key === "Enter" && onClick()}
    className="bg-gray-800 rounded-2xl p-3 flex flex-col hover:bg-gray-700 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
    aria-label={`Play ${video.title} by ${video.creator}`}
  >
    <div className="relative aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
      <FontAwesomeIcon icon={faPlayCircle} className="text-blue-400 text-4xl" />
    </div>
    <h3 className="text-white font-semibold mt-2 line-clamp-2">{video.title}</h3>
    <p className="text-gray-400 text-sm mt-1">
      {video.creator} • {video.views} views
    </p>
  </div>
);

// Sub-component: Video Grid
const VideoGrid = ({ videos, onVideoClick, loading = false }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-gray-800 rounded-2xl p-3 animate-pulse">
            <div className="bg-gray-900 rounded-lg h-32"></div>
            <div className="h-4 bg-gray-700 rounded mt-2"></div>
            <div className="h-3 bg-gray-700 rounded w-3/4 mt-1"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!videos.length) {
    return (
      <div className="text-center py-12 text-gray-400">
        No videos found. Try a different search.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {videos.map((video) => (
        <VideoCard key={video.id} video={video} onClick={() => onVideoClick(video)} />
      ))}
    </div>
  );
};

// Sub-component: Video Modal
const VideoModal = ({ video, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" role="dialog" aria-modal="true">
      <div className="bg-gray-900 rounded-lg p-4 max-w-4xl w-full mx-4 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-white text-2xl"
          aria-label="Close video"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="text-white font-semibold mb-2">{video.title}</h2>
        <p className="text-gray-400 mb-4">{video.creator} • {video.views} views</p>
        <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <p className="text-white mb-2">Simulated Video Player</p>
            <div className="bg-gray-700 rounded p-4">
              <p className="text-sm text-gray-300">Playing: {video.title}</p>
              <p className="text-xs text-gray-500 mt-1">Duration: 5:30 • HD</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-component: Upload Form
const UploadForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    monetizeAds: false,
    monetizeShopping: false,
    file: null,
  });

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert("Title is required!");
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl p-4 mt-4">
      <h3 className="text-white font-semibold mb-3">Upload New Video</h3>
      <div className="flex flex-col gap-3">
        <div>
          <label htmlFor="title" className="block text-gray-300 text-sm mb-1">
            Title *
          </label>
          <input
            id="title"
            name="title"
            type="text"
            value={formData.title}
            onChange={handleChange}
            className="w-full bg-gray-900 text-white p-2 rounded-lg outline-none focus:border-blue-500 border border-gray-700"
            required
            aria-required="true"
          />
        </div>
        <div>
          <label htmlFor="description" className="block text-gray-300 text-sm mb-1">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full bg-gray-900 text-white p-2 rounded-lg outline-none focus:border-blue-500 border border-gray-700"
          />
        </div>
        <div>
          <label htmlFor="file" className="block text-gray-300 text-sm mb-1">
            Video File
          </label>
          <input
            id="file"
            name="file"
            type="file"
            accept="video/*"
            onChange={handleChange}
            className="w-full text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
          />
        </div>
        <label className="flex items-center gap-2 text-gray-300">
          <input
            type="checkbox"
            name="monetizeAds"
            checked={formData.monetizeAds}
            onChange={handleChange}
          />
          Monetize with Ads
        </label>
        <label className="flex items-center gap-2 text-gray-300">
          <input
            type="checkbox"
            name="monetizeShopping"
            checked={formData.monetizeShopping}
            onChange={handleChange}
          />
          Monetize with Shopping Ads
        </label>
        <div className="flex gap-2">
          <button
            type="submit"
            className="flex-1 bg-blue-500 text-white rounded-lg py-2 font-medium hover:bg-blue-600 transition"
          >
            Upload Video
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 bg-gray-700 text-white rounded-lg py-2 hover:bg-gray-600 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

// Sub-component: Tab Navigation
const TabNav = ({ activeTab, onTabChange }) => {
  const tabs = [
    { key: "videos", icon: faList, label: "Videos" },
    { key: "myvideos", icon: faUpload, label: "My Videos" },
    { key: "playlist", icon: faStar, label: "My Playlist" },
    { key: "popular", icon: faPlayCircle, label: "Popular" },
    { key: "profile", icon: faUser, label: "My Profile" },
  ];

  return (
    <nav role="tablist" className="flex justify-around border-b border-gray-800 py-2 text-sm overflow-x-auto">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={activeTab === tab.key}
          aria-controls={`${tab.key}-panel`}
          onClick={() => onTabChange(tab.key)}
          className={`flex items-center gap-1 px-2 py-1 rounded-t-lg transition ${
            activeTab === tab.key
              ? "text-white border-b-2 border-blue-400"
              : "text-gray-200 hover:text-gray-300"
          }`}
        >
          <FontAwesomeIcon icon={tab.icon} />
          {/* <span className="hidden sm:inline">{tab.label}</span> */}
          <span className="text-xs lg:text-m text-white">{tab.label}</span>
        </button>
      ))}
    </nav>
  );
};

// Sub-component: Modal Wrapper
const Modal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" role="dialog" aria-modal="true">
      <div className="bg-gray-900 rounded-lg p-4 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-white text-2xl"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        {title && <h2 className="text-white font-semibold mb-4">{title}</h2>}
        {children}
      </div>
    </div>
  );
};

// Sub-component: Profile Sections
const MyProfileSection = ({ onEdit }) => {
  const userData = {
    name: "John Doe",
    email: "john.doe@example.com",
    bio: "Passionate about sustainable farming and rural innovation.",
    joined: "January 2024",
    subscribers: "1.2K",
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-white font-semibold text-lg">Profile Information</h3>
        <button
          onClick={onEdit}
          className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faEdit} /> Edit
        </button>
      </div>
      <div className="space-y-3">
        <p className="text-gray-300"><strong>Name:</strong> {userData.name}</p>
        <p className="text-gray-300"><strong>Email:</strong> {userData.email}</p>
        <p className="text-gray-300"><strong>Bio:</strong> {userData.bio}</p>
        <p className="text-gray-300"><strong>Joined:</strong> {userData.joined}</p>
        <p className="text-gray-300"><strong>Subscribers:</strong> {userData.subscribers}</p>
      </div>
    </div>
  );
};

const ManageChannelSection = ({ onClose }) => {
  const [channelName, setChannelName] = useState("Think5Whys Agri Channel");
  const [description, setDescription] = useState("Empowering rural entrepreneurs with wisdom.");

  const handleSave = () => {
    alert("Channel updated successfully! (Simulated)");
    onClose();
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-gray-300 text-sm mb-1">Channel Name</label>
        <input
          type="text"
          value={channelName}
          onChange={(e) => setChannelName(e.target.value)}
          className="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-700"
        />
      </div>
      <div>
        <label className="block text-gray-300 text-sm mb-1">Channel Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-700"
        />
      </div>
      <button
        onClick={handleSave}
        className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
      >
        Save Changes
      </button>
    </div>
  );
};

const AccountSettingsSection = ({ onClose }) => {
  const [settings, setSettings] = useState({
    notifications: true,
    privacy: "Public",
    language: "English",
  });

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    alert("Settings updated! (Simulated)");
    onClose();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
        <span className="text-gray-300">Email Notifications</span>
        <input
          type="checkbox"
          checked={settings.notifications}
          onChange={() => handleToggle("notifications")}
          className="toggle toggle-blue"
        />
      </div>
      <div>
        <label className="block text-gray-300 text-sm mb-1">Privacy Mode</label>
        <select
          value={settings.privacy}
          onChange={(e) => setSettings((prev) => ({ ...prev, privacy: e.target.value }))}
          className="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-700"
        >
          <option>Public</option>
          <option>Private</option>
        </select>
      </div>
      <div>
        <label className="block text-gray-300 text-sm mb-1">Language</label>
        <select
          value={settings.language}
          onChange={(e) => setSettings((prev) => ({ ...prev, language: e.target.value }))}
          className="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-700"
        >
          <option>English</option>
          <option>Spanish</option>
          <option>Hindi</option>
        </select>
      </div>
      <button
        onClick={handleSave}
        className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
      >
        Save Settings
      </button>
    </div>
  );
};

const ViewersInsightsSection = () => {
  const chartData = useMemo(
    () => ({
      labels: ["Jan 2025", "Feb 2025", "Mar 2025", "Apr 2025", "May 2025", "Oct 2025"],
      datasets: [
        {
          label: "Views",
          data: [12000, 19000, 3000, 5000, 2000, 15000],
          backgroundColor: "rgba(236, 72, 153, 0.5)",
        },
        {
          label: "Engagement",
          data: [65, 59, 80, 81, 56, 55],
          backgroundColor: "rgba(59, 130, 246, 0.5)",
        },
      ],
    }),
    []
  );

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Monthly Viewers Insights (2025)",
      },
    },
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <h3 className="text-white font-semibold text-lg mb-4">Viewers Insights</h3>
      <div className="h-64">
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 text-gray-300 text-sm">
        <p>Total Views: 67K | Average Engagement: 62%</p>
        <p>Top Video: Wisdom of Soil (12K views)</p>
      </div>
    </div>
  );
};

const ConfigureAdsSection = ({ onClose }) => {
  const [adSettings, setAdSettings] = useState({
    enableAds: true,
    adFrequency: "Every 5 minutes",
    revenueShare: 70,
  });

  const handleSave = () => {
    alert("Ad configuration updated! (Simulated)");
    onClose();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
        <span className="text-gray-300">Enable Ads</span>
        <input
          type="checkbox"
          checked={adSettings.enableAds}
          onChange={(e) => setAdSettings((prev) => ({ ...prev, enableAds: e.target.checked }))}
          className="toggle toggle-blue"
        />
      </div>
      <div>
        <label className="block text-gray-300 text-sm mb-1">Ad Frequency</label>
        <select
          value={adSettings.adFrequency}
          onChange={(e) => setAdSettings((prev) => ({ ...prev, adFrequency: e.target.value }))}
          className="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-700"
        >
          <option>Every 5 minutes</option>
          <option>Every 10 minutes</option>
          <option>Mid-roll only</option>
        </select>
      </div>
      <div>
        <label className="block text-gray-300 text-sm mb-1">Revenue Share (%)</label>
        <input
          type="range"
          min="50"
          max="90"
          value={adSettings.revenueShare}
          onChange={(e) => setAdSettings((prev) => ({ ...prev, revenueShare: parseInt(e.target.value) }))}
          className="w-full"
        />
        <p className="text-gray-400 text-sm">{adSettings.revenueShare}%</p>
      </div>
      <button
        onClick={handleSave}
        className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
      >
        Save Ad Settings
      </button>
    </div>
  );
};

const ContactCreatorSection = ({ onClose }) => {
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (!message.trim()) {
      alert("Please enter a message!");
      return;
    }
    alert("Message sent to creator! (Simulated)");
    setMessage("");
    onClose();
  };

  return (
    <div className="space-y-4">
      <p className="text-gray-300">Contact the foodTube team for in-video ad collaborations.</p>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Describe your ad proposal..."
        rows={4}
        className="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-700"
      />
      <div className="flex gap-2">
        <button
          onClick={handleSend}
          className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
        >
          <FontAwesomeIcon icon={faEnvelope} className="mr-2" /> Send Message
        </button>
        <button
          onClick={onClose}
          className="px-4 bg-gray-700 text-white py-2 rounded-lg hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Sub-component: Profile Menu
const ProfileMenu = ({ onUploadClick, onItemClick }) => {
  const menuItems = [
    { icon: "👤", label: "My Profile", key: "myprofile" },
    { icon: "🎬", label: "Manage Channel", key: "managechannel" },
    { icon: "⚙️", label: "Account Settings", key: "accountsettings" },
    { icon: "⬆️", label: "Upload Video", key: "upload" },
    { icon: "📊", label: "Viewers Insights", key: "insights" },
    { icon: "💰", label: "Configure Ads", key: "ads" },
    { icon: "🤝", label: "Contact Creator for In-Video Ads", key: "contact" },
  ];

  return (
    <div className="grid gap-3 text-gray-300 max-w-md mx-auto">
      {menuItems.map((item) => (
        <button
          key={item.key}
          className="bg-gray-800 p-3 rounded-lg text-left hover:bg-gray-700 transition focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-3"
          onClick={() => onItemClick(item.key)}
        >
          <span className="text-xl">{item.icon}</span>
          {item.label}
        </button>
      ))}
    </div>
  );
};

// Main Component: FoodTube
const FoodTube = () => {
  const [activeTab, setActiveTab] = useState("videos");
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [myVideos, setMyVideos] = useState([]);
  const [profileSubView, setProfileSubView] = useState(null); // For profile sub-sections
  const [showEditProfile, setShowEditProfile] = useState(false);

  // Dummy data with consistent structure
  const dummyVideos = useMemo(
    () => [
      { id: 1, title: "Mastering Indian Spices", creator: "SpiceMaster", views: "12K" },
      { id: 2, title: "Easy Vegan Recipes", creator: "GreenChef", views: "8.4K" },
      { id: 3, title: "Baking 101: From Scratch", creator: "BakeWithLove", views: "6.2K" },
      { id: 4, title: "Street Food Secrets", creator: "FoodExplorer", views: "9.8K" },
      { id: 5, title: "Digital Cooking Hacks", creator: "KitchenTech", views: "11K" },
      { id: 6, title: "Sustainable Home Cooking", creator: "EcoKitchen", views: "7.5K" },
    ],
    []
  );

  const playlist = useMemo(
    () => [
      { id: 1, title: "Top Cooking Picks", creator: "BakeWithLove", views: "5K" },
      { id: 2, title: "Healthy & Quick Meals", creator: "GreenChef", views: "4.2K" },
    ],
    []
  );

  const popularVideos = useMemo(
    () => [
      { id: 1, title: "Street Food Around India", creator: "FoodExplorer", views: "20K" },
      { id: 2, title: "Mastering Desserts", creator: "BakeWithLove", views: "18K" },
      { id: 3, title: "Kitchen Gadgets You Need", creator: "KitchenTech", views: "15K" },
    ],
    []
  );


  // Simulate data fetching
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [activeTab]);

  // Filter videos based on search
  const filteredVideos = useMemo(() => {
    const allVideos = {
      videos: dummyVideos,
      playlist,
      popular: popularVideos,
    }[activeTab] || [];

    return allVideos.filter(
      (v) =>
        v.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.creator.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [activeTab, searchQuery, dummyVideos, playlist, popularVideos]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowUploadForm(false);
    setSearchQuery(""); // Reset search on tab change
    setProfileSubView(null); // Reset profile sub-view
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
  };

  const handleCloseModal = () => {
    setSelectedVideo(null);
  };

  const handleUploadSubmit = (formData) => {
    // Simulate upload
    // console.log("Uploading:", formData);
    alert("Video uploaded successfully! (Simulated)");
    setShowUploadForm(false);
    setMyVideos((prev) => [...prev, { 
      ...formData, 
      id: Date.now(), 
      views: "0",
      creator: "John Doe" // Simulated user
    }]);
  };

  const handleUploadClick = () => {
    setShowUploadForm(true);
    setProfileSubView(null);
  };

  const handleProfileItemClick = (key) => {
    if (key === "upload") {
      handleUploadClick();
      return;
    }
    setProfileSubView(key);
  };

  const handleCloseSubView = () => {
    setProfileSubView(null);
  };

  const handleEditProfile = () => {
    setShowEditProfile(true);
  };

  const handleSaveProfileEdit = () => {
    alert("Profile updated! (Simulated)");
    setShowEditProfile(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200 flex flex-col font-sans">
      <Header />
      {/* <Hero /> */}
      <section className="p-4 text-center bg-gradient-to-r from-blue-800 via-blue-700 to-blue-800">
        <h2 className="text-xl sm:text-2xl text-white font-semibold mb-2">
        Discover. Fun. Learn. Grow. Monetize.
        </h2>
        {/* <p className="text-gray-400 text-sm">
        Explore wisdom through stories, mentors, and creators.
        </p> */}
        {["videos", "popular"].includes(activeTab) && <SearchBar onSearch={handleSearch} />}
      </section>

      <TabNav activeTab={activeTab} onTabChange={handleTabChange} />

      <main className="flex-1 p-4 overflow-y-auto" role="main">
        {activeTab === "videos" && (
          <section id="videos-panel" role="tabpanel" aria-labelledby="videos-tab">
            <VideoGrid videos={filteredVideos} onVideoClick={handleVideoClick} loading={loading} />
          </section>
        )}

        {activeTab === "myvideos" && (
          <section id="myvideos-panel" role="tabpanel" aria-labelledby="myvideos-tab">
            <div className="text-center mt-10">
              {!showUploadForm ? (
                <>
                  <p className="text-gray-400 mb-3">
                    {myVideos.length === 0
                      ? "You have not uploaded any video yet."
                      : `You have ${myVideos.length} uploaded video(s).`}
                  </p>
                  <button
                    onClick={handleUploadClick}
                    className="bg-blue-500 px-4 py-2 rounded-lg text-white font-medium hover:bg-blue-600 transition"
                  >
                    Upload Now
                  </button>
                  {myVideos.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-white font-semibold mb-3">Your Videos</h3>
                      <VideoGrid videos={myVideos} onVideoClick={handleVideoClick} />
                    </div>
                  )}
                </>
              ) : (
                <UploadForm onSubmit={handleUploadSubmit} onCancel={() => setShowUploadForm(false)} />
              )}
            </div>
          </section>
        )}

        {activeTab === "playlist" && (
          <section id="playlist-panel" role="tabpanel" aria-labelledby="playlist-tab">
            <VideoGrid videos={filteredVideos} onVideoClick={handleVideoClick} loading={loading} />
          </section>
        )}

        {activeTab === "popular" && (
          <section id="popular-panel" role="tabpanel" aria-labelledby="popular-tab">
            <VideoGrid videos={filteredVideos} onVideoClick={handleVideoClick} loading={loading} />
          </section>
        )}

        {activeTab === "profile" && (
          <section id="profile-panel" role="tabpanel" aria-labelledby="profile-tab">
            <div className="max-w-4xl mx-auto">
              {!profileSubView ? (
                <ProfileMenu onUploadClick={handleUploadClick} onItemClick={handleProfileItemClick} />
              ) : (
                <div className="mt-6">
                  {profileSubView === "myprofile" && (
                    <>
                      <MyProfileSection onEdit={handleEditProfile} />
                      <Modal isOpen={showEditProfile} onClose={() => setShowEditProfile(false)} title="Edit Profile">
                        <div className="space-y-4">
                          {/* Similar to ManageChannel, but for profile */}
                          <div>
                            <label className="block text-gray-300 text-sm mb-1">Name</label>
                            <input type="text" className="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-700" />
                          </div>
                          <div>
                            <label className="block text-gray-300 text-sm mb-1">Bio</label>
                            <textarea rows={3} className="w-full bg-gray-900 text-white p-2 rounded-lg border border-gray-700" />
                          </div>
                          <button onClick={handleSaveProfileEdit} className="w-full bg-blue-500 text-white py-2 rounded-lg">
                            Save
                          </button>
                        </div>
                      </Modal>
                    </>
                  )}
                  {profileSubView === "managechannel" && (
                    <Modal isOpen={true} onClose={handleCloseSubView} title="Manage Channel">
                      <ManageChannelSection onClose={handleCloseSubView} />
                    </Modal>
                  )}
                  {profileSubView === "accountsettings" && (
                    <Modal isOpen={true} onClose={handleCloseSubView} title="Account Settings">
                      <AccountSettingsSection onClose={handleCloseSubView} />
                    </Modal>
                  )}
                  {profileSubView === "insights" && <ViewersInsightsSection />}
                  {profileSubView === "ads" && (
                    <Modal isOpen={true} onClose={handleCloseSubView} title="Configure Ads">
                      <ConfigureAdsSection onClose={handleCloseSubView} />
                    </Modal>
                  )}
                  {profileSubView === "contact" && (
                    <Modal isOpen={true} onClose={handleCloseSubView} title="Contact Creator">
                      <ContactCreatorSection onClose={handleCloseSubView} />
                    </Modal>
                  )}
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <VideoModal video={selectedVideo} isOpen={!!selectedVideo} onClose={handleCloseModal} />

      <footer className="text-center text-gray-500 text-xs py-3 border-t border-gray-800 bg-gray-900">
        © 2025 foodTube by Think5Whys • Your world of wisdom in motion
      </footer>
    </div>
  );
};

export default FoodTube;